/**
 * CalendarModule - Page component for the Calendar module.
 *
 * Loads calendar events on mount (lazy hydration — data is only fetched when
 * this module activates via React.lazy). Renders the calendar grid with
 * day detail panel and event management.
 *
 * @see Requirements 5.1, 5.4, 5.6, 13.3
 */

import { useEffect, useCallback, useState } from 'react';
import { useCalendarStore } from '@/stores/calendarStore';
import { useTaskStore } from '@/stores/taskStore';
import { CalendarGrid } from '@/components/calendar/CalendarGrid';
import { DayDetailPanel } from '@/components/calendar/DayDetailPanel';
import { EventForm } from '@/components/calendar/EventForm';
import { CalendarConnections } from '@/components/calendar/CalendarConnections';
import { FloatingActionButton } from '@/components/ui/FloatingActionButton';

export default function CalendarModule() {
  const loadEvents = useCalendarStore((s) => s.loadEvents);
  const loadConnections = useCalendarStore((s) => s.loadConnections);
  const visibleRange = useCalendarStore((s) => s.visibleRange);
  const selectedDate = useCalendarStore((s) => s.selectedDate);
  const loadTasks = useTaskStore((s) => s.loadTasks);

  const [showEventForm, setShowEventForm] = useState(false);
  const [showConnections, setShowConnections] = useState(false);

  // Lazy hydration: load events and connections when module mounts
  useEffect(() => {
    loadEvents(visibleRange.start, visibleRange.end);
    loadConnections();
    loadTasks();
  }, [loadEvents, loadConnections, loadTasks, visibleRange.start, visibleRange.end]);

  const handleCreateEvent = useCallback(() => {
    setShowEventForm(true);
    setShowConnections(false);
  }, []);

  const handleShowConnections = useCallback(() => {
    setShowConnections(true);
    setShowEventForm(false);
  }, []);

  const handleCloseForm = useCallback(() => {
    setShowEventForm(false);
  }, []);

  const handleConnectionChange = useCallback(() => {
    // Reload events after a connection change
    loadEvents(visibleRange.start, visibleRange.end);
    loadConnections();
  }, [loadEvents, loadConnections, visibleRange.start, visibleRange.end]);

  return (
    <div className="flex flex-col h-full p-6 gap-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <h1 className="text-heading-1 font-extrabold text-white tracking-tight">Calendar</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleShowConnections}
            className="px-4 py-2 text-sm font-medium rounded-full
                       glass text-text-secondary
                       hover:bg-white/10 hover:text-text-primary
                       transition-all duration-300 ease-in-out
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
            aria-label="Manage calendar connections"
          >
            Connections
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-h-0 flex gap-4">
        {/* Calendar grid */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <CalendarGrid />
        </div>

        {/* Side panel: day detail, event form, or connections */}
        <div className="w-[360px] flex-shrink-0 card-gradient overflow-y-auto transition-all duration-200 ease-in-out p-4">
          {showEventForm && (
            <EventForm
              defaultDate={selectedDate}
              onSuccess={handleCloseForm}
              onCancel={handleCloseForm}
            />
          )}
          {showConnections && !showEventForm && (
            <CalendarConnections onConnectionChange={handleConnectionChange} />
          )}
          {!showEventForm && !showConnections && <DayDetailPanel />}
        </div>
      </div>

      {/* Floating Action Button - shows event form */}
      <FloatingActionButton
        onClick={handleCreateEvent}
        label="+ New Event"
        gradient="teal"
      />
    </div>
  );
}
